'use client';

import React, { useState, useEffect } from 'react';
import { Key, Plus, Copy, ToggleLeft, ToggleRight, Trash2, Phone, MapPin, Search, X, RefreshCw, ShieldCheck } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';

interface AccessCode {
  id: string;
  access_code: string;
  retailer_name: string;
  retailer_phone: string | null;
  retailer_zone: string | null;
  credit_limit: number;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  last_used_at: string | null;
}

function generateCode(name: string): string {
  const prefix = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w.slice(0, 3).toUpperCase())
    .join('-');
  const suffix = Math.floor(1000 + Math.random() * 9000);
  return `${prefix}-${suffix}`;
}

const zones = ['Centre_Ville', 'Khwazat', 'Ville_Haute', 'Guéliz', 'Hivernage', 'Autre'];

export default function CatalogAccessCodesPanel() {
  const [codes, setCodes] = useState<AccessCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    retailer_name: '',
    retailer_phone: '',
    retailer_zone: 'Centre_Ville',
    credit_limit: 5000,
    notes: '',
    access_code: '',
  });

  const supabase = createClient();

  const fetchCodes = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('supplier_catalog_access_codes')
      .select('*')
      .order('created_at', { ascending: false });
    setCodes(data || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchCodes();
  }, []);

  const handleOpenForm = () => {
    setForm({
      retailer_name: '',
      retailer_phone: '',
      retailer_zone: 'Centre_Ville',
      credit_limit: 5000,
      notes: '',
      access_code: '',
    });
    setShowForm(true);
  };

  const handleGenerateCode = () => {
    if (!form.retailer_name.trim()) {
      toast.error('Entrez le nom du détaillant d\'abord');
      return;
    }
    setForm((f) => ({ ...f, access_code: generateCode(f.retailer_name) }));
  };

  const handleSave = async () => {
    if (!form.retailer_name.trim()) { toast.error('Nom du détaillant requis'); return; }
    if (!form.access_code.trim()) { toast.error('Générez ou saisissez un code d\'accès'); return; }
    setSaving(true);
    const { error } = await supabase.from('supplier_catalog_access_codes').insert({
      retailer_name: form.retailer_name,
      retailer_phone: form.retailer_phone || null,
      retailer_zone: form.retailer_zone,
      credit_limit: form.credit_limit,
      notes: form.notes || null,
      access_code: form.access_code.toUpperCase(),
      is_active: true,
    });
    setSaving(false);
    if (error) {
      toast.error(error.message.includes('unique') ? 'Ce code existe déjà — générez-en un autre' : error.message);
    } else {
      toast.success(`Code ${form.access_code.toUpperCase()} créé pour ${form.retailer_name}`);
      setShowForm(false);
      fetchCodes();
    }
  };

  const handleToggle = async (code: AccessCode) => {
    const { error } = await supabase
      .from('supplier_catalog_access_codes')
      .update({ is_active: !code.is_active })
      .eq('id', code.id);
    if (!error) {
      setCodes((prev) => prev.map((c) => c.id === code.id ? { ...c, is_active: !c.is_active } : c));
      toast.success(code.is_active ? 'Code désactivé' : 'Code activé');
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('supplier_catalog_access_codes').delete().eq('id', id);
    if (!error) {
      setCodes((prev) => prev.filter((c) => c.id !== id));
      toast.success('Code supprimé');
    }
  };

  const handleCopy = (code: string) => {
    navigator.clipboard?.writeText(code);
    toast.success(`Code ${code} copié !`);
  };

  const filtered = codes.filter(
    (c) =>
      c.retailer_name.toLowerCase().includes(search.toLowerCase()) ||
      c.access_code.toLowerCase().includes(search.toLowerCase()) ||
      (c.retailer_zone || '').toLowerCase().includes(search.toLowerCase())
  );

  const activeCount = codes.filter((c) => c.is_active).length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Key size={16} className="text-primary" />
            </div>
            <div>
              <h3 className="text-base font-700 text-foreground">Codes d'Accès Catalogue</h3>
              <p className="text-xs text-muted-foreground">{activeCount} codes actifs · {codes.length} total</p>
            </div>
          </div>
        </div>
        <button onClick={handleOpenForm} className="btn-primary text-sm">
          <Plus size={15} />
          Nouveau Code
        </button>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 p-3 bg-primary-light border border-primary/20 rounded-xl">
        <ShieldCheck size={16} className="text-primary flex-shrink-0 mt-0.5" />
        <p className="text-xs text-primary">
          Chaque détaillant reçoit un code unique pour accéder à votre catalogue et passer des commandes directement via le système Gérant.
          Partagez le code via WhatsApp ou SMS — le détaillant l'entre sur la page <strong>/retailer/catalog-access</strong>.
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Rechercher par nom, code ou zone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-base pl-9"
        />
      </div>

      {/* Add form */}
      {showForm && (
        <div className="card-base border-primary/30 bg-primary-light/30 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-700 text-foreground">Nouveau Détaillant</h4>
            <button onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground"><X size={15} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">Nom du Détaillant *</label>
              <input
                type="text"
                placeholder="ex: Snack Atlas Guéliz"
                value={form.retailer_name}
                onChange={(e) => setForm((f) => ({ ...f, retailer_name: e.target.value }))}
                className="input-base"
              />
            </div>
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">Téléphone</label>
              <input
                type="text"
                placeholder="+212 6XX-XXXXXX"
                value={form.retailer_phone}
                onChange={(e) => setForm((f) => ({ ...f, retailer_phone: e.target.value }))}
                className="input-base"
              />
            </div>
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">Zone</label>
              <select
                value={form.retailer_zone}
                onChange={(e) => setForm((f) => ({ ...f, retailer_zone: e.target.value }))}
                className="input-base"
              >
                {zones.map((z) => <option key={z} value={z}>{z.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">Plafond Crédit (MAD)</label>
              <input
                type="number"
                min={0}
                value={form.credit_limit}
                onChange={(e) => setForm((f) => ({ ...f, credit_limit: parseFloat(e.target.value) || 0 }))}
                className="input-base"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-600 text-foreground mb-1">Code d'Accès *</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="ex: ATLAS-2026"
                  value={form.access_code}
                  onChange={(e) => setForm((f) => ({ ...f, access_code: e.target.value.toUpperCase() }))}
                  className="input-base flex-1 font-mono font-700 tracking-widest"
                />
                <button
                  onClick={handleGenerateCode}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border bg-muted text-xs font-600 text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
                >
                  <RefreshCw size={13} />
                  Générer
                </button>
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-600 text-foreground mb-1">Notes (optionnel)</label>
              <input
                type="text"
                placeholder="ex: Client fidèle, paiement différé autorisé..."
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="input-base"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="btn-secondary text-sm">Annuler</button>
            <button onClick={handleSave} disabled={saving} className="btn-primary text-sm">
              {saving ? 'Enregistrement...' : 'Créer le Code'}
            </button>
          </div>
        </div>
      )}

      {/* Codes list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((code) => (
            <div key={code.id} className={`card-base transition-all ${!code.is_active ? 'opacity-60' : ''}`}>
              <div className="flex items-start gap-4">
                {/* Code badge */}
                <div className="flex-shrink-0">
                  <div className={`px-3 py-2 rounded-xl border-2 font-mono font-800 text-sm tracking-widest ${code.is_active ? 'bg-primary-light border-primary text-primary' : 'bg-muted border-border text-muted-foreground'}`}>
                    {code.access_code}
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-700 text-foreground">{code.retailer_name}</p>
                    <span className={`text-2xs font-700 px-2 py-0.5 rounded-full ${code.is_active ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'}`}>
                      {code.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    {code.retailer_phone && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Phone size={10} /> {code.retailer_phone}
                      </span>
                    )}
                    {code.retailer_zone && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin size={10} /> {code.retailer_zone.replace('_', ' ')}
                      </span>
                    )}
                    <span className="text-xs text-info font-600">
                      Crédit: {code.credit_limit.toLocaleString('fr-MA')} MAD
                    </span>
                  </div>
                  {code.notes && <p className="text-xs text-muted-foreground italic mt-1">{code.notes}</p>}
                  {code.last_used_at && (
                    <p className="text-2xs text-muted-foreground mt-1">
                      Dernière utilisation: {new Date(code.last_used_at).toLocaleDateString('fr-MA')}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => handleCopy(code.access_code)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                    title="Copier le code"
                  >
                    <Copy size={14} />
                  </button>
                  <button
                    onClick={() => handleToggle(code)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${code.is_active ? 'hover:bg-warning/10 text-success hover:text-warning' : 'hover:bg-success/10 text-muted-foreground hover:text-success'}`}
                    title={code.is_active ? 'Désactiver' : 'Activer'}
                  >
                    {code.is_active ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                  </button>
                  <button
                    onClick={() => handleDelete(code.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-danger/10 text-muted-foreground hover:text-danger transition-colors"
                    title="Supprimer ce code"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {filtered.length === 0 && (
            <div className="text-center py-12 bg-card border border-border rounded-xl">
              <Key size={32} className="text-muted-foreground mx-auto mb-3 opacity-30" />
              <p className="text-sm text-muted-foreground">
                {search ? 'Aucun code trouvé pour cette recherche' : 'Aucun code créé — cliquez sur "Nouveau Code"'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
